/**
 * Parse parameters from OpenSCAD code
 * 
 * OpenSCAD parameters are defined at the top of the file as:
 * paramName = value; // optional comment with range [min:step:max] or options [opt1, opt2]
 * 
 * This parser extracts these parameters so they can be displayed as editable hotspots.
 */

export interface ParsedParameter {
  name: string;
  displayName: string;
  value: number | string | boolean;
  defaultValue: number | string | boolean;
  type: 'number' | 'string' | 'boolean';
  description?: string;
  range?: {
    min?: number;
    max?: number;
    step?: number;
  };
}

/**
 * Parse OpenSCAD code and extract parameter definitions
 */
export function parseOpenSCADParameters(code: string): ParsedParameter[] {
  if (!code) {
    return [];
  }

  // Only look at the code before any module or function definitions
  // Parameters are typically at the top of the file
  const headerCode = code.split(/^(module |function )/m)[0];

  const parameters: ParsedParameter[] = [];
  
  // Match parameter definitions: name = value; // optional comment
  const parameterRegex = /^([a-zA-Z_][a-zA-Z0-9_]*)\s*=\s*([^;]+);[\t\f ]*(?:\/\/(.*))?/gm;
  
  let match;
  while ((match = parameterRegex.exec(headerCode)) !== null) {
    const name = match[1];
    const rawValue = match[2].trim();
    const comment = match[3]?.trim();
    
    // Skip special OpenSCAD variables that aren't really parameters
    if (name.startsWith('$') && name !== '$fn') continue;
    
    // Parse the value and determine type
    const typeAndValue = parseValue(rawValue);
    if (!typeAndValue) continue;
    
    // Skip if value references another variable or is an expression
    if (isExpression(rawValue)) continue;
    
    const { value, type } = typeAndValue;
    
    // Parse range/description from comment
    let range: ParsedParameter['range'] = undefined;
    let description: string | undefined = undefined;
    
    if (comment) {
      const rangeMatch = comment.match(/\[([^\]]+)\]/);
      if (rangeMatch) {
        const rangeStr = rangeMatch[1];
        // Check if it's a range like [min:step:max] or [min:max]
        if (rangeStr.includes(':')) {
          const parts = rangeStr.split(':').map(p => parseFloat(p.trim()));
          if (parts.length === 3 && parts.every(p => !isNaN(p))) {
            range = { min: parts[0], step: parts[1], max: parts[2] };
          } else if (parts.length === 2 && parts.every(p => !isNaN(p))) {
            range = { min: parts[0], max: parts[1] };
          }
        }
        // Remove range from description
        description = comment.replace(/\[([^\]]+)\]/, '').trim() || undefined;
      } else {
        description = comment;
      }
    }
    
    // If no range specified, create a reasonable default for numbers
    if (!range && type === 'number') {
      const numVal = value as number;
      range = {
        min: 0,
        max: Math.max(numVal * 3, 100),
        step: numVal < 10 ? 0.5 : 1
      };
    }
    
    // Generate display name from parameter name
    const displayName = name
      .replace(/_/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    
    parameters.push({
      name,
      displayName,
      value,
      defaultValue: value,
      type,
      description,
      range,
    });
  }
  
  return parameters;
}

/**
 * Parse a raw value string and return the typed value
 */
function parseValue(rawValue: string): { value: number | string | boolean; type: 'number' | 'string' | 'boolean' } | null {
  // Number
  if (/^-?\d+(\.\d+)?$/.test(rawValue)) {
    return { value: parseFloat(rawValue), type: 'number' };
  }
  
  // Boolean
  if (rawValue === 'true') {
    return { value: true, type: 'boolean' };
  }
  if (rawValue === 'false') {
    return { value: false, type: 'boolean' };
  }
  
  // String (quoted)
  if (/^".*"$/.test(rawValue)) {
    return { value: rawValue.slice(1, -1), type: 'string' };
  }
  
  return null;
}

/**
 * Check if a value is an expression (references other variables or uses operators)
 */
function isExpression(value: string): boolean {
  // Skip if starts with letter (variable reference) but isn't a boolean
  if (value !== 'true' && value !== 'false') {
    if (/^[a-zA-Z_]/.test(value) && !/^".*"$/.test(value)) {
      return true;
    }
  }
  
  // Skip if contains operators (except unary minus for negative numbers)
  if (/[+*/]/.test(value)) {
    return true;
  }
  
  // Skip if contains parentheses (function calls)
  if (/[()]/.test(value)) {
    return true;
  }
  
  // Skip arrays (we only support scalar parameters for hotspots)
  if (value.startsWith('[') && value.endsWith(']')) {
    return true;
  }
  
  return false;
}

/**
 * Update OpenSCAD code with new parameter values
 */
export function updateParameterInCode(code: string, name: string, newValue: number | string | boolean): string {
  // Match the parameter definition
  const paramRegex = new RegExp(`(^|\\n)(${name})\\s*=\\s*[^;]+;`, 'm');
  
  // Format the value appropriately
  let formattedValue: string;
  if (typeof newValue === 'string') {
    formattedValue = `"${newValue}"`;
  } else {
    formattedValue = String(newValue);
  }
  
  return code.replace(paramRegex, `$1${name} = ${formattedValue};`);
}
